import { useEffect, useState } from "react";
import { ActivityIndicator, View } from "react-native";
import {
  Dialog,
  DialogHeader,
  FieldMessage,
  PrimaryButton,
  SecondaryButton,
  MoonFormField,
  MoonValueInput,
} from "@louise/moon-ui";
import { useLocale, useTranslations } from "@src/packages/i18n";
import { formatDate } from "@src/packages/i18n/formatters";
import type { Locale } from "@src/packages/i18n/locale";
import { LabValueField } from "@src/packages/test-results/components/LabValueField";
import { EMBER } from "../../../../onboarding/theme";
import type { MonitoringEntryDraft } from "../suggested/MonitoringEntryModal";

export type MonitoringCellKey = "follicles" | "lining" | "e2" | "lh" | "p4";

export function isMonitoringCellKey(value: string): value is MonitoringCellKey {
  return ["follicles", "lining", "e2", "lh", "p4"].includes(value);
}

type SimpleCellConfig = {
  draftKey: "liningThickness" | "estradiol" | "lh" | "progesterone";
  titleKey: string;
  fieldLabelKey: string;
  unit: string;
  unitKey?: "estradiolUnit" | "lhUnit" | "progesteroneUnit";
  /** The lab marker, whose units the field offers. */
  marker?: string;
  placeholderKey: string;
  max: number;
};

const SIMPLE_CELL_CONFIG: Record<
  Exclude<MonitoringCellKey, "follicles">,
  SimpleCellConfig
> = {
  lining: {
    draftKey: "liningThickness",
    titleKey:
      "care.overview.calendar.monitoring_log.modal.cell_edit.lining_title",
    fieldLabelKey:
      "care.overview.calendar.monitoring_log.modal.cell_edit.lining_label",
    unit: "mm",
    placeholderKey:
      "care.overview.calendar.monitoring_log.modal.lining_placeholder",
    max: 50,
  },
  e2: {
    draftKey: "estradiol",
    titleKey:
      "care.overview.calendar.monitoring_log.modal.cell_edit.estradiol_title",
    fieldLabelKey:
      "care.overview.calendar.monitoring_log.modal.cell_edit.estradiol_label",
    unit: "pg/mL",
    unitKey: "estradiolUnit",
    marker: "Estradiol",
    placeholderKey:
      "care.overview.calendar.monitoring_log.modal.cell_edit.value_placeholder",
    max: 10000,
  },
  lh: {
    draftKey: "lh",
    titleKey: "care.overview.calendar.monitoring_log.modal.cell_edit.lh_title",
    fieldLabelKey:
      "care.overview.calendar.monitoring_log.modal.cell_edit.lh_label",
    unit: "mIU/mL",
    unitKey: "lhUnit",
    marker: "LH",
    placeholderKey:
      "care.overview.calendar.monitoring_log.modal.cell_edit.value_placeholder",
    max: 100,
  },
  p4: {
    draftKey: "progesterone",
    titleKey:
      "care.overview.calendar.monitoring_log.modal.cell_edit.progesterone_title",
    fieldLabelKey:
      "care.overview.calendar.monitoring_log.modal.cell_edit.progesterone_label",
    unit: "ng/mL",
    unitKey: "progesteroneUnit",
    marker: "Progesterone",
    placeholderKey:
      "care.overview.calendar.monitoring_log.modal.cell_edit.value_placeholder",
    max: 50,
  },
};

function parseSizeList(value: string): number[] {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
    .map(Number)
    .filter((item) => Number.isFinite(item));
}

function sizeListError(value: string, label: string): string | null {
  if (!value.trim()) return null;
  const tokens = value.split(",").map((item) => item.trim());
  if (tokens.some((item) => !item || !/^\d+(\.\d+)?$/.test(item))) {
    return `${label} must use comma-separated numbers.`;
  }
  if (parseSizeList(value).some((item) => item < 0 || item > 50)) {
    return `${label} must be between 0 and 50 mm.`;
  }
  return null;
}

function numberError(value: string, label: string, max: number): string | null {
  if (!value.trim()) return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > max) {
    return `${label} must be between 0 and ${max}.`;
  }
  return null;
}

function derivedCountAfterSizeEdit(
  previousSizes: string,
  previousCount: string,
  nextSizes: string,
): string {
  if (nextSizes.trim()) return String(parseSizeList(nextSizes).length);
  if (
    previousSizes.trim() &&
    previousCount === String(parseSizeList(previousSizes).length)
  ) {
    return "";
  }
  return previousCount;
}

function formatMonitoringDate(dateISO: string, locale: Locale): string {
  const date = new Date(`${dateISO}T12:00:00`);
  if (Number.isNaN(date.getTime())) return dateISO;
  return formatDate(date, locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function MonitoringCellDetailDialog({
  visible,
  dateISO,
  cellKey,
  current,
  onClose,
  onSave,
}: {
  visible: boolean;
  dateISO: string;
  cellKey: MonitoringCellKey | null;
  current: MonitoringEntryDraft;
  onClose: () => void;
  onSave: (patch: Partial<MonitoringEntryDraft>) => Promise<void>;
}) {
  const t = useTranslations();
  const { locale } = useLocale();
  const [value, setValue] = useState("");
  const [secondaryValue, setSecondaryValue] = useState("");
  const [unit, setUnit] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!visible || !cellKey) return;
    if (cellKey === "follicles") {
      setValue(current.leftSizes);
      setSecondaryValue(current.rightSizes);
    } else {
      const cellConfig = SIMPLE_CELL_CONFIG[cellKey];
      setValue(current[cellConfig.draftKey]);
      setSecondaryValue("");
      setUnit(
        cellConfig.unitKey
          ? current[cellConfig.unitKey] || cellConfig.unit
          : cellConfig.unit,
      );
    }
    setError(null);
  }, [cellKey, current, visible]);

  if (!visible || !cellKey) return null;

  const dateLabel = formatMonitoringDate(dateISO, locale);
  const isFollicleCell = cellKey === "follicles";
  const config = isFollicleCell ? null : SIMPLE_CELL_CONFIG[cellKey];

  const handleSave = async () => {
    let validationError: string | null = null;
    if (isFollicleCell) {
      const leftError = sizeListError(
        value,
        t("care.overview.calendar.monitoring_log.modal.cell_edit.left_ovary"),
      );
      const rightError = sizeListError(
        secondaryValue,
        t("care.overview.calendar.monitoring_log.modal.cell_edit.right_ovary"),
      );
      validationError = leftError ?? rightError;
      if (!validationError && !value.trim() && !secondaryValue.trim()) {
        validationError = t(
          "care.overview.calendar.monitoring_log.modal.cell_edit.value_required",
        );
      }
    } else if (config) {
      validationError = numberError(value, t(config.fieldLabelKey), config.max);
      if (!validationError && !value.trim()) {
        validationError = t(
          "care.overview.calendar.monitoring_log.modal.cell_edit.value_required",
        );
      }
    }
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      if (isFollicleCell) {
        await onSave({
          leftSizes: value,
          rightSizes: secondaryValue,
          leftCount: derivedCountAfterSizeEdit(
            current.leftSizes,
            current.leftCount,
            value,
          ),
          rightCount: derivedCountAfterSizeEdit(
            current.rightSizes,
            current.rightCount,
            secondaryValue,
          ),
        });
      } else if (config) {
        await onSave({
          [config.draftKey]: value,
          ...(config.unitKey ? { [config.unitKey]: unit } : null),
        });
      }
      onClose();
    } catch (saveError) {
      setError(
        saveError instanceof Error
          ? saveError.message
          : t(
              "care.overview.calendar.monitoring_log.modal.cell_edit.save_error",
            ),
      );
    } finally {
      setSaving(false);
    }
  };

  const title = isFollicleCell
    ? t("care.overview.calendar.monitoring_log.modal.cell_edit.follicles_title")
    : t(config!.titleKey);
  const subtitle = t(
    "care.overview.calendar.monitoring_log.modal.cell_edit.subtitle",
    { params: { date: dateLabel } },
  );

  return (
    <Dialog
      visible={visible}
      onClose={saving ? () => undefined : onClose}
      closeButton="minimal"
      testID="monitoring-cell-dialog"
      surfaceStyle={{ maxHeight: 560 }}
      contentStyle={{
        paddingTop: 20,
        paddingHorizontal: 20,
        paddingBottom: 18,
        maxHeight: 560,
      }}
    >
      <DialogHeader
        title={title}
        subtitle={subtitle}
        style={{ marginBottom: 16 }}
      />
      <View style={{ gap: 12 }}>
        {isFollicleCell ? (
          <>
            <MoonFormField
              label={t(
                "care.overview.calendar.monitoring_log.modal.cell_edit.left_ovary",
              )}
              labelHint={t(
                "care.overview.calendar.monitoring_log.modal.comma_separated",
              )}
            >
              <MoonValueInput
                value={value}
                onChangeText={setValue}
                keyboardType="decimal-pad"
                suffix="mm"
                placeholder={t(
                  "care.overview.calendar.monitoring_log.modal.sizes_placeholder",
                )}
                accessibilityLabel={t(
                  "care.overview.calendar.monitoring_log.modal.cell_edit.left_ovary",
                )}
              />
            </MoonFormField>
            <MoonFormField
              label={t(
                "care.overview.calendar.monitoring_log.modal.cell_edit.right_ovary",
              )}
              labelHint={t(
                "care.overview.calendar.monitoring_log.modal.comma_separated",
              )}
            >
              <MoonValueInput
                value={secondaryValue}
                onChangeText={setSecondaryValue}
                keyboardType="decimal-pad"
                suffix="mm"
                placeholder={t(
                  "care.overview.calendar.monitoring_log.modal.sizes_placeholder",
                )}
                accessibilityLabel={t(
                  "care.overview.calendar.monitoring_log.modal.cell_edit.right_ovary",
                )}
              />
            </MoonFormField>
          </>
        ) : config?.marker ? (
          // The monitoring blood value field, its unit choice inside it.
          <LabValueField
            label={t(config.fieldLabelKey)}
            value={value}
            onChangeValue={setValue}
            unit={unit}
            onChangeUnit={setUnit}
            marker={config.marker}
            numeric
            placeholder={t(config.placeholderKey)}
            unitAction="pick_bloodwork_unit"
          />
        ) : (
          <MoonFormField label={t(config!.fieldLabelKey)}>
            <MoonValueInput
              value={value}
              onChangeText={setValue}
              keyboardType="decimal-pad"
              suffix={config!.unit}
              placeholder={t(config!.placeholderKey)}
              accessibilityLabel={t(config!.fieldLabelKey)}
            />
          </MoonFormField>
        )}
        {error ? <FieldMessage>{error}</FieldMessage> : null}
      </View>
      <View style={{ flexDirection: "row", gap: 8, marginTop: 20 }}>
        <SecondaryButton
          flex={1}
          label={t(
            "care.overview.calendar.monitoring_log.modal.cell_edit.cancel",
          )}
          onPress={onClose}
          disabled={saving}
        />
        <PrimaryButton
          flex={1.35}
          label={t(
            "care.overview.calendar.monitoring_log.modal.cell_edit.save",
          )}
          onPress={() => void handleSave()}
          disabled={saving}
          icon={
            saving ? (
              <ActivityIndicator color={EMBER.colors.textLight} size="small" />
            ) : undefined
          }
        />
      </View>
    </Dialog>
  );
}
